import React, { useState, useEffect, useCallback, useContext } from 'react';
import { api } from './api/api';
import { AuthContext } from '../App';

const PAGE_SIZE = 50;

// ── Generic sortable header ──────────────────────────────────────────────

const SortableTh = ({ label, sortKey, activeKey, dir, onSort, style }) => (
  <th style={{ cursor: 'pointer', userSelect: 'none', ...style }} onClick={() => onSort(sortKey)}>
    {label}{activeKey === sortKey ? (dir === 'desc' ? ' ▼' : ' ▲') : ''}
  </th>
);

// ── Generic filter-row editor ────────────────────────────────────────────
// Lets the user add {field, min, max} rows instead of one static input per
// column (there are 12-16 numeric columns per tab — a fixed grid of inputs
// would be huge).

const FilterEditor = ({ fields, rows, setRows }) => {
  const addRow = () => setRows(r => [...r, { field: fields[0].key, min: '', max: '' }]);
  const removeRow = (i) => setRows(r => r.filter((_, idx) => idx !== i));
  const updateRow = (i, patch) => setRows(r => r.map((row, idx) => idx === i ? { ...row, ...patch } : row));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {rows.map((row, i) => (
        <div key={i} style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <select className="fb-select" style={{ fontSize: 12, padding: '4px 24px 4px 8px' }}
            value={row.field} onChange={e => updateRow(i, { field: e.target.value })}>
            {fields.map(f => <option key={f.key} value={f.key}>{f.label}</option>)}
          </select>
          <input type="number" placeholder="min" value={row.min}
            onChange={e => updateRow(i, { min: e.target.value })}
            className="fb-input fb-input-sm" style={{ width: 70 }} />
          <span style={{ color: 'var(--c-text-muted)', fontSize: 12 }}>–</span>
          <input type="number" placeholder="max" value={row.max}
            onChange={e => updateRow(i, { max: e.target.value })}
            className="fb-input fb-input-sm" style={{ width: 70 }} />
          <button type="button" onClick={() => removeRow(i)} title="Remove filter"
            style={{ border: 'none', background: 'none', color: 'var(--c-text-muted)', cursor: 'pointer', fontSize: 14, padding: '0 4px' }}>
            ×
          </button>
        </div>
      ))}
      <button type="button" className="fb-btn fb-btn-ghost fb-btn-sm" style={{ alignSelf: 'flex-start' }} onClick={addRow}>
        + Add filter
      </button>
    </div>
  );
};

const buildFiltersPayload = (rows) => {
  const filters = {};
  rows.forEach(({ field, min, max }) => {
    if (min === '' && max === '') return;
    filters[field] = {
      ...(min !== '' && { min: parseFloat(min) }),
      ...(max !== '' && { max: parseFloat(max) }),
    };
  });
  return filters;
};

// ── Pagination bar ────────────────────────────────────────────────────────

const PaginationBar = ({ page, totalPages, totalElements, onPageChange }) => (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 16px', borderTop: '1px solid var(--c-border-light)' }}>
    <span style={{ fontSize: 12, color: 'var(--c-text-muted)' }}>
      {totalElements != null ? `${totalElements} total` : ''}
      {totalPages != null ? ` · page ${page + 1}/${Math.max(totalPages, 1)}` : ''}
    </span>
    <div style={{ display: 'flex', gap: 6 }}>
      <button type="button" className="fb-btn fb-btn-ghost fb-btn-sm" disabled={page <= 0} onClick={() => onPageChange(page - 1)}>
        ← Prev
      </button>
      <button type="button" className="fb-btn fb-btn-ghost fb-btn-sm" disabled={totalPages != null && page >= totalPages - 1} onClick={() => onPageChange(page + 1)}>
        Next →
      </button>
    </div>
  </div>
);

// ── Shared paged/filtered market table hook ──────────────────────────────

const useMarketSearch = (endpoint) => {
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(null);
  const [totalElements, setTotalElements] = useState(null);
  const [sortKey, setSortKey] = useState('overall');
  const [sortDir, setSortDir] = useState('desc');
  const [name, setName] = useState('');
  const [filterRows, setFilterRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const search = useCallback(async (opts = {}) => {
    setLoading(true);
    setError(null);
    const targetPage = opts.page ?? page;
    try {
      const res = await api.post(endpoint, {
        page: targetPage,
        size: PAGE_SIZE,
        sortBy: sortKey,
        sortDir,
        name: name.trim() || undefined,
        filters: buildFiltersPayload(filterRows),
      });
      const data = res.data || {};
      setRows(data.content || []);
      setPage(data.page ?? targetPage);
      setTotalPages(data.totalPages ?? null);
      setTotalElements(data.totalElements ?? null);
    } catch (err) {
      setError(err.response?.status === 403 ? 'Access denied.'
        : err.response?.status === 404 ? 'Search endpoint not available yet.'
        : 'Failed to load.');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endpoint, sortKey, sortDir, name, filterRows]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { search({ page: 0 }); }, [sortKey, sortDir]);

  const onSort = (key) => {
    if (key === sortKey) setSortDir(d => d === 'desc' ? 'asc' : 'desc');
    else { setSortKey(key); setSortDir('desc'); }
  };

  const onPageChange = (p) => search({ page: p });
  const onSearchClick = () => search({ page: 0 });

  return {
    rows, page, totalPages, totalElements, sortKey, sortDir, onSort,
    name, setName, filterRows, setFilterRows, loading, error,
    onPageChange, onSearchClick,
  };
};

// ── Drivers tab ───────────────────────────────────────────────────────────

const DRIVER_FILTER_FIELDS = [
  { key: 'overall', label: 'Overall' }, { key: 'concentration', label: 'Concentration' },
  { key: 'talent', label: 'Talent' }, { key: 'aggressiveness', label: 'Aggressiveness' },
  { key: 'experience', label: 'Experience' }, { key: 'ti', label: 'TI' },
  { key: 'stamina', label: 'Stamina' }, { key: 'charisma', label: 'Charisma' },
  { key: 'motivation', label: 'Motivation' }, { key: 'reputation', label: 'Reputation' },
  { key: 'age', label: 'Age' }, { key: 'weight', label: 'Weight' },
  { key: 'delta', label: 'Delta' }, { key: 'salary', label: 'Salary' },
  { key: 'fee', label: 'Fee' }, { key: 'offers', label: 'Offers' },
];

const DRIVER_COLS = [
  { key: 'overall', label: 'Overall', bold: true },
  { key: 'concentration', label: 'Conc.' },
  { key: 'talent', label: 'Talent' },
  { key: 'aggressiveness', label: 'Aggr.' },
  { key: 'experience', label: 'Exp.' },
  { key: 'ti', label: 'TI' },
  { key: 'stamina', label: 'Stamina' },
  { key: 'charisma', label: 'Charisma' },
  { key: 'motivation', label: 'Motiv.' },
  { key: 'reputation', label: 'Rep.' },
  { key: 'age', label: 'Age' },
  { key: 'weight', label: 'Weight' },
  { key: 'delta', label: 'Delta' },
  { key: 'salary', label: 'Salary' },
  { key: 'fee', label: 'Fee' },
  { key: 'offers', label: 'Offers' },
];

const DriversTab = () => {
  const {
    rows, page, totalPages, totalElements, sortKey, sortDir, onSort,
    name, setName, filterRows, setFilterRows, loading, error,
    onPageChange, onSearchClick,
  } = useMarketSearch('/drivers/search');

  const flat = rows.map(r => ({ ...r.driver, delta: r.delta }));

  return (
    <div className="fb-card">
      <div className="fb-card-header" style={{ flexWrap: 'wrap', gap: 10 }}>
        <span className="fb-card-title">Drivers</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <input type="text" placeholder="Search by name…" value={name}
            onChange={e => setName(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && onSearchClick()}
            className="fb-input fb-input-sm" style={{ width: 180 }} />
          <button type="button" className="fb-btn fb-btn-primary fb-btn-sm" onClick={onSearchClick}>Search</button>
        </div>
      </div>
      <div className="fb-card-body" style={{ borderBottom: '1px solid var(--c-border-light)' }}>
        <FilterEditor fields={DRIVER_FILTER_FIELDS} rows={filterRows} setRows={setFilterRows} />
      </div>

      {loading && <div style={{ padding: 16, color: 'var(--c-text-muted)', fontSize: 13 }}>Loading…</div>}
      {!loading && error && <div style={{ padding: 16, color: 'var(--c-error)', fontSize: 13 }}>{error}</div>}
      {!loading && !error && (
        <>
          <div className="fb-table-wrap ra-table-wrap">
            <table className="fb-table fb-table-sm ra-table" style={{ whiteSpace: 'nowrap' }}>
              <thead>
                <tr>
                  <SortableTh label="Name" sortKey="name" activeKey={sortKey} dir={sortDir} onSort={onSort} style={{ minWidth: 160 }} />
                  <th>Nat</th>
                  {DRIVER_COLS.map(c => (
                    <SortableTh key={c.key} label={c.label} sortKey={c.key} activeKey={sortKey} dir={sortDir} onSort={onSort} />
                  ))}
                </tr>
              </thead>
              <tbody>
                {flat.map(d => (
                  <tr key={d.id}>
                    <td>
                      <a href={`https://gpro.net/DriverProfile.asp?ID=${d.id}`} target="_blank" rel="noreferrer"
                        style={{ color: 'var(--c-accent)', fontWeight: 600 }}>
                        {d.name}
                      </a>
                    </td>
                    <td style={{ color: 'var(--c-text-muted)' }}>{d.nat ?? '—'}</td>
                    {DRIVER_COLS.map(c => (
                      <td key={c.key} style={{ fontWeight: c.bold ? 700 : 400 }}>{d[c.key] ?? '—'}</td>
                    ))}
                  </tr>
                ))}
                {flat.length === 0 && (
                  <tr><td colSpan={DRIVER_COLS.length + 2} style={{ color: 'var(--c-text-muted)', textAlign: 'center', padding: 20 }}>No drivers match.</td></tr>
                )}
              </tbody>
            </table>
          </div>
          <PaginationBar page={page} totalPages={totalPages} totalElements={totalElements} onPageChange={onPageChange} />
        </>
      )}
    </div>
  );
};

// ── Technical Directors tab ──────────────────────────────────────────────

const TD_FILTER_FIELDS = [
  { key: 'overall', label: 'Overall' }, { key: 'leadership', label: 'Leadership' },
  { key: 'mechanics', label: 'Mechanics' }, { key: 'electronics', label: 'Electronics' },
  { key: 'aerodynamics', label: 'Aerodynamics' }, { key: 'experience', label: 'Experience' },
  { key: 'pitcoordination', label: 'Pit Coordination' }, { key: 'motivation', label: 'Motivation' },
  { key: 'age', label: 'Age' }, { key: 'salary', label: 'Salary' },
  { key: 'fee', label: 'Fee' }, { key: 'offers', label: 'Offers' },
];

const TD_COLS = [
  { key: 'overall', label: 'Overall', bold: true },
  { key: 'leadership', label: 'Lead.' },
  { key: 'mechanics', label: 'Mech.' },
  { key: 'electronics', label: 'Elec.' },
  { key: 'aerodynamics', label: 'Aero.' },
  { key: 'experience', label: 'Exp.' },
  { key: 'pitcoordination', label: 'PitCoord' },
  { key: 'motivation', label: 'Motiv.' },
  { key: 'age', label: 'Age' },
  { key: 'salary', label: 'Salary' },
  { key: 'fee', label: 'Fee' },
  { key: 'offers', label: 'Offers' },
];

const TDsTab = () => {
  const { userRole } = useContext(AuthContext);
  const canUpdate = userRole === 'ADMIN' || userRole === 'UPDATER';

  const {
    rows, page, totalPages, totalElements, sortKey, sortDir, onSort,
    name, setName, filterRows, setFilterRows, loading, error,
    onPageChange, onSearchClick,
  } = useMarketSearch('/tds/search');

  const [updating, setUpdating] = useState(false);
  const [updateMsg, setUpdateMsg] = useState(null);

  const handleUpdate = async () => {
    setUpdating(true);
    setUpdateMsg(null);
    try {
      await api.get('/update/tds');
      onSearchClick();
      setUpdateMsg('Updated.');
    } catch (err) {
      setUpdateMsg(err.response?.status === 403 ? 'Access denied.' : 'Update failed.');
    } finally {
      setUpdating(false);
    }
  };

  return (
    <div className="fb-card">
      <div className="fb-card-header" style={{ flexWrap: 'wrap', gap: 10 }}>
        <span className="fb-card-title">Technical Directors</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {updateMsg && <span style={{ fontSize: 12, color: 'var(--c-text-muted)' }}>{updateMsg}</span>}
          {canUpdate && (
            <button type="button" className="fb-btn fb-btn-ghost fb-btn-sm" disabled={updating} onClick={handleUpdate}>
              {updating ? 'Updating…' : 'Update'}
            </button>
          )}
          <input type="text" placeholder="Search by name…" value={name}
            onChange={e => setName(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && onSearchClick()}
            className="fb-input fb-input-sm" style={{ width: 180 }} />
          <button type="button" className="fb-btn fb-btn-primary fb-btn-sm" onClick={onSearchClick}>Search</button>
        </div>
      </div>
      <div className="fb-card-body" style={{ borderBottom: '1px solid var(--c-border-light)' }}>
        <FilterEditor fields={TD_FILTER_FIELDS} rows={filterRows} setRows={setFilterRows} />
      </div>

      {loading && <div style={{ padding: 16, color: 'var(--c-text-muted)', fontSize: 13 }}>Loading…</div>}
      {!loading && error && <div style={{ padding: 16, color: 'var(--c-error)', fontSize: 13 }}>{error}</div>}
      {!loading && !error && (
        <>
          <div className="fb-table-wrap ra-table-wrap">
            <table className="fb-table fb-table-sm ra-table" style={{ whiteSpace: 'nowrap' }}>
              <thead>
                <tr>
                  <SortableTh label="Name" sortKey="name" activeKey={sortKey} dir={sortDir} onSort={onSort} style={{ minWidth: 160 }} />
                  <th>Nat</th>
                  {TD_COLS.map(c => (
                    <SortableTh key={c.key} label={c.label} sortKey={c.key} activeKey={sortKey} dir={sortDir} onSort={onSort} />
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map(t => (
                  <tr key={t.id}>
                    <td style={{ fontWeight: 600 }}>{t.name}</td>
                    <td style={{ color: 'var(--c-text-muted)' }}>{t.nat ?? '—'}</td>
                    {TD_COLS.map(c => (
                      <td key={c.key} style={{ fontWeight: c.bold ? 700 : 400 }}>{t[c.key] ?? '—'}</td>
                    ))}
                  </tr>
                ))}
                {rows.length === 0 && (
                  <tr><td colSpan={TD_COLS.length + 2} style={{ color: 'var(--c-text-muted)', textAlign: 'center', padding: 20 }}>No technical directors match.</td></tr>
                )}
              </tbody>
            </table>
          </div>
          <PaginationBar page={page} totalPages={totalPages} totalElements={totalElements} onPageChange={onPageChange} />
        </>
      )}
    </div>
  );
};

// ── Main page ─────────────────────────────────────────────────────────────

const Market = () => {
  const [tab, setTab] = useState('drivers');

  const tabStyle = (active) => ({
    padding: '9px 20px',
    cursor: 'pointer',
    border: 'none',
    background: 'none',
    fontWeight: active ? 700 : 400,
    fontSize: 13,
    color: active ? 'var(--c-accent)' : 'var(--c-text-muted)',
    borderBottom: `2px solid ${active ? 'var(--c-accent)' : 'transparent'}`,
    marginBottom: -2,
    transition: 'color 0.15s',
  });

  return (
    <>
      <div className="fb-page-header">
        <div className="fb-page-title">Driver / TD Market</div>
        <div className="fb-page-sub">Current market snapshot — refreshed every race</div>
      </div>

      <div style={{ display: 'flex', borderBottom: '2px solid var(--c-border)', marginBottom: 16 }}>
        <button style={tabStyle(tab === 'drivers')} onClick={() => setTab('drivers')}>Drivers</button>
        <button style={tabStyle(tab === 'tds')} onClick={() => setTab('tds')}>Technical Directors</button>
      </div>

      {tab === 'drivers' && <DriversTab />}
      {tab === 'tds' && <TDsTab />}
    </>
  );
};

export default Market;
